import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Crown, CreditCard, Loader2, ArrowRight, Languages, Check, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const Account = () => {
  const { user, logout, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [languages, setLanguages] = useState([{ code: "auto", name: "Auto-detect" }]);
  const [lang, setLang] = useState(user?.preferred_language || "auto");
  const [langBusy, setLangBusy] = useState(false);
  const [langSaved, setLangSaved] = useState(false);
  const [sub, setSub] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [prov, setProv] = useState(null);
  const [provBusy, setProvBusy] = useState(false);

  const provision = async () => {
    setProvBusy(true);
    try {
      const { data } = await api.post("/billing/provision");
      setProv(data);
      toast.success(`Products ready on ${data.account.display_name || data.account.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reach Stripe");
    } finally { setProvBusy(false); }
  };

  useEffect(() => { setLang(user?.preferred_language || "auto"); }, [user?.preferred_language]);
  useEffect(() => {
    api.get("/languages").then(({ data }) => setLanguages(data.languages || [])).catch(() => {});
    api.get("/billing/subscription").then(({ data }) => setSub(data)).catch(() => {});
  }, []);

  const resync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.get("/billing/subscription", { params: { force: true } });
      setSub(data);
      await refresh();
      toast.success(data.subscription_status ? `Stripe says: ${data.subscription_status} · ${data.plan.toUpperCase()}` : "No subscription found on Stripe");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reach Stripe");
    } finally { setSyncing(false); }
  };

  if (!user) return null;

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to open billing portal");
      setBusy(false);
    }
  };

  const saveLang = async (code) => {
    setLang(code);
    setLangBusy(true);
    setLangSaved(false);
    try {
      await api.post("/settings/language", { language: code });
      await refresh();
      setLangSaved(true);
      setTimeout(() => setLangSaved(false), 1800);
    } catch (err) {
      toast.error("Failed to save language");
    } finally {
      setLangBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto">
      <header className="mb-10">
        <div className="label-mono mb-3">/ account</div>
        <h1 className="font-display text-4xl md:text-5xl tightest font-semibold leading-none">{user.display_name}</h1>
        <div className="text-neutral-400 mt-3 text-[0.92rem]">@{user.username} · {user.email}</div>
      </header>

      <div className="glass-strong p-8 space-y-1 mb-6" data-testid="account-billing-card">
        <Row label="Plan" value={(sub?.plan || user.plan)?.toUpperCase()} />
        {(sub?.subscription_status || user.subscription_status) && (
          <Row label="Subscription" value={sub?.subscription_status || user.subscription_status} />
        )}
        {sub?.renews_at && (
          <Row
            label={sub.cancel_at_period_end ? "Access ends" : "Renews"}
            value={new Date(sub.renews_at).toLocaleDateString()}
          />
        )}
        {sub?.amount ? <Row label="Price" value={`£${sub.amount.toFixed(2)} / month`} /> : null}
        <Row label="Recordings this month" value={`${user.usage?.recordings || 0}`} />
        <Row label="Generations this month" value={`${user.usage?.generations || 0}`} />
        <div className="pt-4 flex items-center gap-3 flex-wrap">
          <button onClick={resync} disabled={syncing} className="btn-secondary text-[0.78rem] py-1.5 px-3" data-testid="billing-resync">
            {syncing ? <><Loader2 size={12} className="animate-spin"/> Checking Stripe…</> : <><RefreshCw size={12}/> Re-check with Stripe</>}
          </button>
          <button onClick={provision} disabled={provBusy} className="btn-ghost text-[0.78rem] py-1.5 px-3" data-testid="billing-provision">
            {provBusy ? <><Loader2 size={12} className="animate-spin"/> Setting up…</> : <>Set up products on Stripe</>}
          </button>
          <span className="text-[0.72rem] text-neutral-500">Paid but still on Free? Re-check fixes it instantly.</span>
        </div>
        {prov?.account && (
          <div className="mt-4 border-t border-white/[0.06] pt-4 text-[0.75rem] text-neutral-500 space-y-1" data-testid="stripe-account-info">
            <div>Stripe account: <span className="text-white">{prov.account.display_name || prov.account.business_name || prov.account.id}</span> {prov.account.livemode ? "(live)" : "(test)"}</div>
            <div className="font-mono text-[0.7rem]">{prov.account.id} · {prov.account.email}</div>
            <div className="font-mono text-[0.7rem]">starter {prov.prices?.starter} · pro {prov.prices?.pro}</div>
          </div>
        )}
      </div>

      {/* Language preference */}
      <div className="glass-strong p-8 mb-6" data-testid="lang-pref-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 border border-white/[0.08] bg-white/[0.02] flex items-center justify-center rounded-[2px] shrink-0">
            <Languages size={14} className="text-amber-400" />
          </div>
          <div>
            <div className="text-[0.95rem] font-medium mb-1">Transcript language</div>
            <div className="text-[0.78rem] text-neutral-400 leading-relaxed max-w-md">
              Vertisiya transcribes in 90+ languages. Set your default here — you can still override per-recording. Notes, flashcards & practice tests are generated in the same language as the transcript.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="input-cyber max-w-xs"
            value={lang}
            onChange={(e) => saveLang(e.target.value)}
            disabled={langBusy}
            data-testid="lang-pref-select"
          >
            {languages.map((l) => (
              <option key={l.code} value={l.code} className="bg-black">{l.name}</option>
            ))}
          </select>
          {langBusy && <Loader2 size={13} className="animate-spin text-neutral-400"/>}
          {langSaved && !langBusy && (
            <span className="text-[0.72rem] text-emerald-400 inline-flex items-center gap-1" data-testid="lang-pref-saved">
              <Check size={12}/> saved
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {user.plan === "free" ? (
          <Link to="/pricing">
            <button className="btn-primary" data-testid="account-upgrade">
              <Crown size={13}/> Upgrade plan <ArrowRight size={13}/>
            </button>
          </Link>
        ) : (
          <button onClick={openPortal} disabled={busy} className="btn-primary" data-testid="account-portal">
            {busy ? <Loader2 size={13} className="animate-spin"/> : <CreditCard size={13}/>}
            Manage billing
          </button>
        )}
        <button onClick={logout} className="btn-secondary" data-testid="account-logout">Sign out</button>
      </div>

      {user.plan !== "free" && (
        <p className="text-[0.76rem] text-neutral-500 mt-6 max-w-md leading-relaxed">
          Manage billing opens Stripe&apos;s secure portal where you can update your card, download invoices, or cancel anytime.
        </p>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center border-b border-white/[0.06] py-3 last:border-b-0">
    <span className="label-mono">{label}</span>
    <span className="text-[0.9rem]">{value}</span>
  </div>
);

export default Account;
